import React, { memo, useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  type SxProps,
  type Theme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { alpha } from '@mui/material/styles';
import { useEngagementMetrics, type EngagementMetricsData } from '../../hooks/useEngagementMetrics';
import type { EngagementTimeRange } from '../../utils/engagementAnalytics';

type MetricValueKey =
  | 'avgOpenRate'
  | 'avgClickRate'
  | 'clickToOpenRate'
  | 'engagementRate'
  | 'uniqueOpens'
  | 'avgOpensPerRecipient'
  | 'engagedRecipients'
  | 'avgTimeToFirstOpen';

type MetricChangeKey =
  | 'openRateChange'
  | 'clickRateChange'
  | 'clickToOpenChange'
  | 'engagementRateChange'
  | 'uniqueOpensChange'
  | 'avgOpensChange'
  | 'engagedRecipientsChange'
  | 'timeToOpenChange';

type MetricTrendKey =
  | 'openRateTrend'
  | 'clickRateTrend'
  | 'clickToOpenTrend'
  | 'engagementRateTrend'
  | 'uniqueOpensTrend'
  | 'avgOpensTrend'
  | 'engagedRecipientsTrend'
  | 'timeToOpenTrend';

type ValueFormat = 'percent' | 'number' | 'duration';

interface MetricCardConfig {
  key: MetricValueKey;
  label: string;
  description: string;
  format: ValueFormat;
  changeKey: MetricChangeKey;
  trendKey: MetricTrendKey;
  inverse?: boolean;
}

const INDUSTRY_BENCHMARKS: Record<MetricValueKey, number> = {
  avgOpenRate: 21.5,
  avgClickRate: 2.9,
  clickToOpenRate: 12.3,
  engagementRate: 25.0,
  uniqueOpens: 650,
  avgOpensPerRecipient: 1.4,
  engagedRecipients: 420,
  avgTimeToFirstOpen: 75,
};

const METRIC_CARDS: MetricCardConfig[] = [
  {
    key: 'avgOpenRate',
    label: 'Open Rate',
    description: 'Share of delivered emails opened',
    format: 'percent',
    changeKey: 'openRateChange',
    trendKey: 'openRateTrend',
  },
  {
    key: 'avgClickRate',
    label: 'Click Rate',
    description: 'Percentage of deliveries with clicks',
    format: 'percent',
    changeKey: 'clickRateChange',
    trendKey: 'clickRateTrend',
  },
  {
    key: 'clickToOpenRate',
    label: 'Click-to-Open',
    description: 'Clicks relative to opens',
    format: 'percent',
    changeKey: 'clickToOpenChange',
    trendKey: 'clickToOpenTrend',
  },
  {
    key: 'engagementRate',
    label: 'Engagement Rate',
    description: 'Unique opens or clicks vs deliveries',
    format: 'percent',
    changeKey: 'engagementRateChange',
    trendKey: 'engagementRateTrend',
  },
  {
    key: 'uniqueOpens',
    label: 'Unique Opens',
    description: 'Recipients who opened at least once',
    format: 'number',
    changeKey: 'uniqueOpensChange',
    trendKey: 'uniqueOpensTrend',
  },
  {
    key: 'avgOpensPerRecipient',
    label: 'Avg Opens / Recipient',
    description: 'How often engaged recipients re-open',
    format: 'number',
    changeKey: 'avgOpensChange',
    trendKey: 'avgOpensTrend',
  },
  {
    key: 'engagedRecipients',
    label: 'Engaged Recipients',
    description: 'Openers or clickers in the period',
    format: 'number',
    changeKey: 'engagedRecipientsChange',
    trendKey: 'engagedRecipientsTrend',
  },
  {
    key: 'avgTimeToFirstOpen',
    label: 'Avg Time to First Open',
    description: 'Seconds between delivery and first open',
    format: 'duration',
    changeKey: 'timeToOpenChange',
    trendKey: 'timeToOpenTrend',
    inverse: true,
  },
];

interface EngagementMetricsOverviewProps {
  timeRange: EngagementTimeRange;
  refreshToken?: number;
  sx?: SxProps<Theme>;
}

const formatValue = (value: number, format: ValueFormat): string => {
  if (!Number.isFinite(value)) {
    return '0';
  }

  switch (format) {
    case 'percent':
      return `${value.toFixed(1)}%`;
    case 'duration': {
      if (value < 60) {
        return `${Math.round(value)}s`;
      }
      const minutes = value / 60;
      if (minutes < 60) {
        return `${minutes.toFixed(1)}m`;
      }
      const hours = minutes / 60;
      return `${hours.toFixed(1)}h`;
    }
    default:
      return value % 1 === 0
        ? value.toLocaleString()
        : value.toFixed(1);
  }
};

const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
  if (trend === 'up') {
    return <TrendingUpIcon color="success" fontSize="small" data-testid="trend-up" />;
  }
  if (trend === 'down') {
    return <TrendingDownIcon color="error" fontSize="small" data-testid="trend-down" />;
  }
  return <RemoveIcon color="disabled" fontSize="small" data-testid="trend-stable" />;
};

const getBenchmarkLabel = (
  value: number,
  benchmark: number,
  format: ValueFormat,
  inverse?: boolean
) => {
  if (!Number.isFinite(value) || !Number.isFinite(benchmark)) {
    return 'No benchmark available';
  }

  const delta = inverse ? benchmark - value : value - benchmark;
  if (Math.abs(delta) < 0.01) {
    return 'On par with industry';
  }

  const sign = delta >= 0 ? '+' : '-';
  return `${sign}${formatValue(Math.abs(delta), format)} vs industry`;
};

const getBenchmarkColor = (
  value: number,
  benchmark: number,
  inverse?: boolean
): 'success' | 'warning' | 'default' => {
  if (!Number.isFinite(value) || !Number.isFinite(benchmark)) {
    return 'default';
  }
  const delta = inverse ? benchmark - value : value - benchmark;
  if (Math.abs(delta) < 0.01) {
    return 'default';
  }
  return delta >= 0 ? 'success' : 'warning';
};

const metricTileBaseSx: SxProps<Theme> = (theme) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 3,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  p: 2,
  minHeight: { xs: 'auto', md: 200 },
});

interface LoadingStateProps {
  tileSx: SxProps<Theme>;
}

const LoadingState: React.FC<LoadingStateProps> = ({ tileSx }) => (
  <Box className="metric-cards" data-testid="engagement-metrics-loading">
    {METRIC_CARDS.map((config) => (
      <Box key={config.key} sx={tileSx}>
        <Stack spacing={1.25}>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={48} />
          <Skeleton variant="rectangular" height={28} sx={{ borderRadius: 1 }} />
        </Stack>
      </Box>
    ))}
  </Box>
);

const EmptyState: React.FC = () => (
  <Box
    sx={{
      py: 6,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1,
    }}
  >
    <InfoOutlinedIcon color="primary" sx={{ fontSize: 40, opacity: 0.6 }} />
    <Typography variant="subtitle1" color="text.secondary">
      No engagement metrics available for this range.
    </Typography>
  </Box>
);

const EngagementMetricsOverview: React.FC<EngagementMetricsOverviewProps> = ({
  timeRange,
  refreshToken = 0,
  sx,
}) => {
  const theme = useTheme();
  const { data, isLoading, error, refresh } = useEngagementMetrics(timeRange);
  const refreshSignalRef = useRef(refreshToken);

  const getTileBackground = (trend?: 'up' | 'down' | 'stable') => {
    if (trend === 'up') {
      return alpha(theme.palette.success.light, 0.11);
    }
    if (trend === 'down') {
      return alpha(theme.palette.error.light, 0.11);
    }
    return theme.palette.background.paper;
  };

  useEffect(() => {
    if (refreshSignalRef.current !== refreshToken) {
      refreshSignalRef.current = refreshToken;
      refresh();
    }
  }, [refreshToken, refresh]);

  const cards = useMemo(() => {
    if (!data) {
      return [];
    }

    return METRIC_CARDS.map((config) => {
      const benchmark = INDUSTRY_BENCHMARKS[config.key];
      const value = data[config.key];
      const change = data[config.changeKey];
      const trend = data[config.trendKey];

      return (
        <Box
          key={config.key}
          component="article"
          sx={[metricTileBaseSx, { backgroundColor: getTileBackground(trend) }]}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                {config.label}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {formatValue(value, config.format)}
              </Typography>
            </Box>
            <Tooltip title={`Change ${config.inverse ? '(lower is better)' : '(higher is better)'}`}>
              <Stack direction="column" alignItems="center" spacing={0.25} sx={{ ml: 1 }}>
                {getTrendIcon(trend)}
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {change}
                </Typography>
              </Stack>
            </Tooltip>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, flex: 1 }}>
            {config.description}
          </Typography>

          <Chip
            size="small"
            color={getBenchmarkColor(value, benchmark, config.inverse)}
            label={getBenchmarkLabel(value, benchmark, config.format, config.inverse)}
            sx={{ fontWeight: 600, mt: 'auto' }}
          />
        </Box>
      );
    });
  }, [data, theme]);

  return (
    <Box data-testid="engagement-metrics-overview" sx={sx}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <IconButton
              aria-label="Retry loading metrics"
              color="inherit"
              size="small"
              onClick={refresh}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        >
          {error.message}
        </Alert>
      )}

      {isLoading && <LoadingState tileSx={metricTileBaseSx} />}
      {!isLoading && !data && !error && <EmptyState />}
      {!isLoading && data && (
        <Box
          data-testid="engagement-metrics-grid"
          className="metric-cards"
        >
          {cards}
        </Box>
      )}
    </Box>
  );
};

export default memo(EngagementMetricsOverview);
